<template>
  <v-col cols="12">
    <Loader
      v-if="loading"
      :key="generateUniqueId()"
      :loader-state="loading"
    ></Loader>

    <v-card>
      <v-card-title>Import des poids des campings</v-card-title>

      <v-card-text>
        <v-alert
          type="info"
          outlined
          class="mb-4"
        >
          <div class="text-body-2">
            <strong>Format du fichier XLSX attendu :</strong>
            <ul class="mt-2 mb-0">
              <li>ID_SH (ID Secureholiday)</li>
              <li>NOM_CAMPING</li>
              <li>Rang (nombre entier)</li>
            </ul>
            <p class="mt-2 mb-0">
              Le fichier doit contenir un en-tête avec ces colonnes : ID_SH, NOM_CAMPING, Rang.
            </p>
          </div>
        </v-alert>

        <v-form ref="form">
          <v-file-input
            v-model="xlsxFile"
            label="Sélectionner un fichier XLSX"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            prepend-icon="mdi-file-document-outline"
            show-size
            :rules="fileRules"
            @change="onFileChange"
          ></v-file-input>

          <v-btn
            color="primary"
            :disabled="!xlsxFile || loading"
            :loading="loading"
            @click="uploadXlsx"
          >
            <v-icon left>
              {{ icons.mdiUpload }}
            </v-icon>
            Importer
          </v-btn>
        </v-form>
      </v-card-text>

      <Snackbar
        v-if="successMessage"
        :snackbar-state="snackbar"
        :success-message="successMessage"
      ></Snackbar>

      <v-card-text
        v-if="uploadResult"
        class="pt-0"
      >
        <v-alert
          :type="uploadResult.hasErrors ? 'warning' : 'success'"
          outlined
          class="mt-4"
        >
          <div class="text-body-1 font-weight-bold mb-2">
            {{ uploadResult.message }}
          </div>
          <div
            v-if="uploadResult.results"
            class="text-body-2"
          >
            <div class="mb-1">
              <strong>Total :</strong> {{ uploadResult.results.total }} ligne{{ uploadResult.results.total > 1 ? 's' : '' }}
            </div>
            <div class="mb-1">
              <strong>Mis à jour :</strong> {{ uploadResult.results.updated }} camping{{ uploadResult.results.updated > 1 ? 's' : '' }}
            </div>
            <div
              v-if="uploadResult.results.not_found > 0"
              class="mb-2"
            >
              <strong>Non trouvés :</strong> {{ uploadResult.results.not_found }} camping{{ uploadResult.results.not_found > 1 ? 's' : '' }}
              <ul
                v-if="getNotFoundCampings().length > 0"
                class="mt-1 mb-0 pl-4"
              >
                <li
                  v-for="(error, index) in getNotFoundCampings()"
                  :key="'not-found-' + index"
                >
                  {{ error.name || 'Sans nom' }} (ID: {{ error.secureHolidayId }})
                </li>
              </ul>
            </div>
            <div
              v-if="uploadResult.results.errors && uploadResult.results.errors.length > 0"
              class="mb-1"
            >
              <strong>Erreurs :</strong> {{ uploadResult.results.errors.length }} erreur{{ uploadResult.results.errors.length > 1 ? 's' : '' }}
              <ul
                v-if="uploadResult.results.errors.length > 0"
                class="mt-1 mb-0 pl-4"
              >
                <li
                  v-for="(error, index) in uploadResult.results.errors"
                  :key="'error-' + index"
                >
                  <span v-if="error.secureHolidayId || error.name">
                    {{ error.name || 'Sans nom' }} (ID: {{ error.secureHolidayId || 'N/A' }}) - 
                  </span>
                  {{ error.message }}
                </li>
              </ul>
            </div>
          </div>
        </v-alert>
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script>
import { mdiUpload } from '@mdi/js'
import Vue from 'vue'
import config from '../../config'
import Loader from '@/components/Common/Loader'
import Snackbar from '@/components/Common/Snackbar'

export default {
  components: { Loader, Snackbar },
  data: () => ({
    loading: false,
    xlsxFile: null,
    uploadResult: null,
    snackbar: false,
    successMessage: null,
    icons: {
      mdiUpload
    },
    fileRules: [
      v => !!v || 'Veuillez sélectionner un fichier XLSX',
      v => !v || (typeof v.name === 'string' && v.name.toLowerCase().endsWith('.xlsx')) || 'Le fichier doit être un XLSX'
    ]
  }),
  methods: {
    generateUniqueId () {
      return Date.now() + Math.random()
    },
    onFileChange (file) {
      if (file) {
        this.uploadResult = null
      }
    },
    getNotFoundCampings () {
      if (!this.uploadResult || !this.uploadResult.results || !this.uploadResult.results.errors) {
        return []
      }

      return this.uploadResult.results.errors.filter(error =>
        error.message && error.message.includes('non trouvé')
      )
    },
    uploadXlsx () {
      if (!this.xlsxFile) {
        return
      }

      // Validation du fichier
      if (typeof this.xlsxFile.name !== 'string' || !this.xlsxFile.name.toLowerCase().endsWith('.xlsx')) {
        this.successMessage = 'Le fichier doit être un XLSX'
        this.snackbar = true
        setTimeout(() => {
          this.snackbar = false
          this.successMessage = null
        }, 3000)

        return
      }

      this.loading = true
      this.uploadResult = null

      const formData = new FormData()
      formData.append('xlsx', this.xlsxFile)

      Vue.prototype.$http
        .post(config.apiUrl + '/api/campings/upload-weight-xlsx', formData, {
          headers: {
            Authorization: 'Bearer ' + this.$store.getters.getToken,
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(response => {
          if (response.status === 200) {
            this.uploadResult = {
              message: response.data.message,
              results: response.data.results,
              hasErrors: response.data.results.errors && response.data.results.errors.length > 0
            }

            this.successMessage = 'Import terminé avec succès'
            this.snackbar = true
            setTimeout(() => {
              this.snackbar = false
              this.successMessage = null
            }, 3000)

            // Réinitialiser le fichier
            this.xlsxFile = null
            this.$refs.form.reset()
          }
        })
        .catch(error => {
          let errorMessage = 'Erreur lors de l\'import du fichier XLSX'

          if (error.response && error.response.data) {
            if (error.response.data.error) {
              errorMessage = error.response.data.error
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message
            } else if (error.response.data.violations && error.response.data.violations.length > 0) {
              errorMessage = error.response.data.violations[0].title
            }
          }

          this.successMessage = errorMessage
          this.snackbar = true
          setTimeout(() => {
            this.snackbar = false
            this.successMessage = null
          }, 3000)

          if (error.response && error.response.data && error.response.data.results) {
            this.uploadResult = {
              message: error.response.data.message || errorMessage,
              results: error.response.data.results,
              hasErrors: true
            }
          }
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
